import { useState, useReducer, useEffect, SyntheticEvent, useRef, Reducer, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import localforage from 'localforage';
import { DomainAdd, Check, Lock, ArrowBack, Add } from '@mui/icons-material';
import BasePaper from '@/components/ui/BasePaper';
import BaseCard from '@/components/ui/BaseCard';
import BaseTitle from '@/components/ui/BaseTitle';
import BaseButton from '@/components/ui/BaseButton';
import BaseLoading from '@/components/ui/BaseLoading';
import {
    Divider,
    FormGroup,
    FormControlLabel,
    Checkbox,
    Box,
    Chip,
    OutlinedInput,
    TextField,
    Typography,
} from '@mui/material';
import {
    getRoleListAPI,
    createAccountAPI,
    fetchClusterListAPI,
    fetchAccountInfoAPI,
    editAccountAPI,
    syncUserRolesAPI,
} from '@/api/admin/rbac';
import { useAppContext } from '@/provider/contexts';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import '@/styles/admin.scss';
import { BaseCluster } from '@/types/pages/admin/cluster';
import {
    AdminAccountDialogValidation,
    AdminAccountFormState,
    AdminAccountFormAction,
    EditAccountInfo,
    DriveEditInfo,
    SaveResult,
    ValidationMessage,
    FieldConfig,
} from '@/types/pages/admin/account';
import { useCheckPermission } from '@/utils/permissionUtils';
import BaseAutocomplete from '@/components/form/BaseAutocomplete';
import { AccountFormData, GetRoleListResponse } from '@/types/pages/admin/apiRBAC';
import { AdminChangePasswordDialog } from './AdminChangePasswordDialog';
import { useTranslation } from 'react-i18next';
import BaseLabelFieldControl from '@/components/form/BaseLabelFieldControl';
import { isEqual, sortBy } from 'lodash';
import { isValidEmail } from '@/utils/emailUtils';

const NORMAL_USER_LEVEL = 1;
const fieldsGridSx = {
    display: 'grid',
    mt: 2,
    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
    gap: 2,
    width: '100%',
    '& .MuiOutlinedInput-root': {
        minHeight: '50px',
    },
};
const borderedBoxSx = {
    p: 2,
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 2,
};
const passwordCardSx = {
    ...borderedBoxSx,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 2,
    mt: 3,
};
const rolesFormGroupSx = {
    ...borderedBoxSx,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 1.5,
};
const initialValidatedMessage: AdminAccountDialogValidation = {
    username: '',
    password: '',
    email: '',
    user_level: '',
    cluster: '',
};

const userLevelOptionsDefault = [
    { value: 1, label: 'Normal User' },
    { value: 2, label: 'Admin' },
    { value: 3, label: 'Super Admin' },
];

const FIELD_CONFIG: FieldConfig[] = [
    {
        htmlFor: 'username',
        label: 'User Name',
        required: true,
        type: 'input',
        inputProps: {
            maxLength: 64,
        },
    },
    {
        htmlFor: 'email',
        label: 'Email',
        required: true,
        type: 'input',
        inputProps: {
            maxLength: 255,
        },
    },
    {
        htmlFor: 'user_level',
        label: 'User Level',
        required: true,
        type: 'select',
    },
    {
        htmlFor: 'cluster',
        label: 'Cluster',
        type: 'autocomplete',
    },
];

const REQUIRED_TEXT_FIELDS = FIELD_CONFIG.filter((field) => field.type === 'input' && field.required);

const reducer: Reducer<AdminAccountFormState, AdminAccountFormAction> = (state, action) => {
    switch (action.type) {
        case 'SET_PAGE_LOADING':
            return { ...state, pageLoading: action.payload };
        case 'SET_SUBMITTING':
            return { ...state, submitting: action.payload };
        case 'SET_FORM_FIELD':
            return { ...state, sForm: { ...state.sForm, [action.field]: action.value } };
        case 'SET_USER_LEVEL':
            return { ...state, sForm: { ...state.sForm, user_level: action.payload } };
        case 'SET_CLUSTERS':
            return { ...state, sForm: { ...state.sForm, clusters: action.payload } };
        case 'TOGGLE_ROLE': {
            const { roleId, checked } = action.payload;
            const userRoleIds = checked
                ? [...state.sForm.userRoleIds, roleId]
                : state.sForm.userRoleIds.filter((existingId) => existingId !== roleId);
            userRoleIds.sort((a, b) => a - b);
            return { ...state, sForm: { ...state.sForm, userRoleIds } };
        }
        case 'UPDATE_FORM':
            return { ...state, sForm: { ...state.sForm, ...action.payload } };
        case 'LOAD_ACCOUNT_DATA':
            return {
                ...state,
                sForm: { ...state.sForm, ...action.payload },
                originalData: action.payload,
            };
        case 'SET_ROLE_LIST':
            return { ...state, allRoleList: action.payload };
        case 'SET_CLUSTER_LIST':
            return { ...state, clusterList: action.payload };
        case 'SET_VALIDATION_MESSAGE':
            return { ...state, validatedMessage: { ...state.validatedMessage, ...action.payload } };
        case 'RESET_VALIDATION_MESSAGE':
            return { ...state, validatedMessage: initialValidatedMessage };
        case 'RESET':
            return {
                ...state,
                sForm: {
                    username: '',
                    password: '',
                    email: '',
                    user_level: NORMAL_USER_LEVEL,
                    user_status: '',
                    clusters: [],
                    store_ids: [],
                    userRoleIds: [],
                },
                userLevelOptions: [],
                originalData: {} as AccountFormData,
                allRoleList: [],
                clusterList: [],
                validatedMessage: initialValidatedMessage,
            };
        default:
            return state;
    }
};

const AdminAccountPageDialog = () => {
    const { id } = useParams();
    const { setNotification } = useAppContext();
    const { t } = useTranslation('common');
    const nav = useNavigate();

    const [openPwdDialog, setOpenPwdDialog] = useState(false);
    const checkPermission = useCheckPermission();
    const originalDataRef = useRef<AccountFormData | null>(null);

    const getUserLevelOption = () => {
        const userDetails = localStorage.getItem('userDetails') as string;
        const currentUserLevel = userDetails ? JSON.parse(userDetails)?.user_level || 1 : 1;

        return userLevelOptionsDefault.filter((option) => option.value <= currentUserLevel);
    };

    const defaultState: AdminAccountFormState = {
        pageLoading: false,
        submitting: false,
        sForm: {
            username: '',
            password: '',
            email: '',
            user_level: NORMAL_USER_LEVEL,
            user_status: '',
            clusters: [],
            store_ids: [],
            userRoleIds: [],
        },
        originalData: {} as AccountFormData,
        allRoleList: [],
        clusterList: [],
        userLevelOptions: getUserLevelOption(),
        validatedMessage: initialValidatedMessage,
    };

    const [{ pageLoading, submitting, sForm, allRoleList, clusterList, userLevelOptions, validatedMessage }, dispatch] =
        useReducer(reducer, defaultState);

    const displayTitle = id ? t('Edit Account') : t('New Account');
    const confirmTitle = id ? t('Save') : t('Create');
    const isPasswordSet = Boolean(id) || Boolean(sForm.password);
    const isAccountReady = !id || Boolean(originalDataRef.current);
    const showRoles = !pageLoading && allRoleList.length > 0 && sForm.user_level === NORMAL_USER_LEVEL;

    useEffect(() => {
        initialize();
    }, []);

    const fetchRoleList = useCallback(async () => {
        try {
            const corporationId = await localforage.getItem('corporation_id');
            const {
                data: { data, isSuccess, message },
            } = await getRoleListAPI(corporationId as number);

            if (isSuccess) {
                return data?.available_roles || [];
            }
            throw new Error(message || 'Failed to fetch role list');
        } catch (error) {
            console.error('Failed to fetch role list:', error);
            const errorReason = error instanceof Error ? error.message : 'Failed to fetch roles.';
            setNotification({ message: errorReason, severity: 'error' });
            return [];
        }
    }, []);

    const fetchClusterList = useCallback(async () => {
        const corporationId = await localforage.getItem('corporation_id');
        try {
            const {
                data: { data, message, isSuccess },
            } = await fetchClusterListAPI({
                cid: corporationId as number,
                f_cluster_name: '',
                current_page: null,
                data_per_page: null,
            });

            if (isSuccess) {
                return data.aaData || [];
            }
            throw new Error(message);
        } catch (error) {
            console.error('Failed to fetch cluster list:', error);
            const errorReason = error instanceof Error ? error.message : 'Failed to fetch cluster list.';
            setNotification({ message: errorReason, severity: 'error' });
            return [];
        }
    }, []);

    const fetchAccountInfo = useCallback(
        async (roleList: GetRoleListResponse['available_roles'], clusterList: BaseCluster[]) => {
            dispatch({ type: 'SET_ROLE_LIST', payload: roleList });
            dispatch({ type: 'SET_CLUSTER_LIST', payload: clusterList });
            if (!id) return;
            try {
                dispatch({ type: 'SET_PAGE_LOADING', payload: true });
                const {
                    data: { data, isSuccess, message },
                } = await fetchAccountInfoAPI(Number(id));

                if (isSuccess) {
                    const clusters = clusterList.filter((clusterItem) =>
                        data.clusters.find((cluster) => cluster.id === clusterItem.id),
                    );

                    const userRoleIds = data.user_roles
                        .map((role) => role.id)
                        .filter((roleId) => roleList.some((role) => role.role_id === roleId))
                        .sort((a, b) => a - b);

                    const formData = {
                        ...data,
                        password: '',
                        clusters,
                        userRoleIds,
                    };
                    originalDataRef.current = formData;
                    dispatch({ type: 'LOAD_ACCOUNT_DATA', payload: formData });
                    return;
                }
                throw new Error(message);
            } catch (error) {
                console.error('Failed to fetch account info:', error);
                const errorReason = error instanceof Error ? error.message : 'Failed to fetch account info.';
                setNotification({ message: errorReason, severity: 'error' });
            } finally {
                dispatch({ type: 'SET_PAGE_LOADING', payload: false });
            }
        },
        [id, setNotification],
    );

    const initialize = async () => {
        dispatch({ type: 'SET_PAGE_LOADING', payload: true });
        const roleList = await fetchRoleList();
        const clusterList = await fetchClusterList();
        await fetchAccountInfo(roleList, clusterList);
        dispatch({ type: 'SET_PAGE_LOADING', payload: false });
    };

    const setAPIValidationMessage = (data: ValidationMessage[]) => {
        data.forEach(({ field, message }) => {
            dispatch({ type: 'SET_VALIDATION_MESSAGE', payload: { [field]: message } });
        });
    };

    const validateForm = () => {
        const errors: AdminAccountDialogValidation = {
            username: '',
            email: '',
            password: '',
            user_level: '',
            cluster: '',
        };
        const missingLabels: string[] = [];

        REQUIRED_TEXT_FIELDS.forEach(({ htmlFor, label }) => {
            const value = (sForm[htmlFor as keyof typeof sForm] as string)?.trim();
            if (!value) {
                errors[htmlFor] = t('Required');
                missingLabels.push(label);
            }
        });

        if (!id && !sForm.password) {
            errors.password = t('Required');
            missingLabels.push('Password');
        }

        if (!isValidEmail(sForm.email)) {
            errors.email = t('Please enter a valid email address.');
            missingLabels.push('Email');
        }
        dispatch({ type: 'SET_VALIDATION_MESSAGE', payload: errors });

        const hasErrors = missingLabels.length > 0;
        if (hasErrors) {
            setNotification({
                message: t(`Please fill in required field(s): {{fields}}`, {
                    fields: missingLabels.join(', '),
                }),
                severity: 'warning',
            });
        }
        return hasErrors;
    };

    const driveEditInfo = (): DriveEditInfo => {
        const originalSnapshot = originalDataRef.current as AccountFormData;
        const editData = {} as EditAccountInfo;

        if (sForm.password) editData.new_password = sForm.password;
        if (sForm.email !== originalSnapshot.email) editData.new_email = sForm.email;
        if (sForm.username !== originalSnapshot.username) editData.new_username = sForm.username;
        if (sForm.user_level !== originalSnapshot.user_level) editData.new_user_level = sForm.user_level;
        if (sForm.user_status !== originalSnapshot.user_status) editData.new_user_status = sForm.user_status;

        const clusterIds = sForm.clusters.map((cluster) => cluster.id);
        const originalClusterIds = originalSnapshot.clusters.map((cluster) => cluster.id);
        if (!isEqual(clusterIds, originalClusterIds)) editData.new_clusters = clusterIds;

        let roleIds: number[] = [];
        let hasRoleChanges = false;
        if (
            editData.new_user_level === NORMAL_USER_LEVEL ||
            (sForm.user_level === NORMAL_USER_LEVEL && originalSnapshot.user_level === NORMAL_USER_LEVEL)
        ) {
            if (!isEqual(sForm.userRoleIds, originalSnapshot.userRoleIds)) {
                hasRoleChanges = true;
                roleIds = sForm.userRoleIds;
            }
        }

        return {
            hasProfileChanges: Object.keys(editData).length > 0,
            editData,
            hasRoleChanges,
            roleIds,
        };
    };

    const submitProfileEdit = async (accountId: string, editData: EditAccountInfo): Promise<SaveResult> => {
        try {
            const {
                data: { data, isSuccess, message, status },
            } = await editAccountAPI(Number(accountId), {
                corporate_id: (await localforage.getItem('corporation_id')) as number,
                ...editData,
            });

            if (status === 200 && isSuccess) {
                return { done: 'success', message: t('Profile updated successfully.') };
            }

            if (status === 400 && message === 'Validation failed.' && Array.isArray(data)) {
                return { done: 'validation', message: 'Validation failed.', validationMessage: data };
            }
            throw new Error(message);
        } catch (error) {
            const errorReason = error instanceof Error ? error.message : 'Edit failed!';
            return { done: 'failure', message: errorReason };
        }
    };

    const submitRoleSync = async (roleIds: number[]): Promise<SaveResult> => {
        try {
            const corporationId = (await localforage.getItem('corporation_id')) as number;
            const {
                data: { isSuccess, message },
            } = await syncUserRolesAPI({
                uid: [Number(id)],
                corporate_id: corporationId,
                role_ids: roleIds,
            });

            if (isSuccess) {
                return { done: 'success', message: 'User Roles updated successfully.' };
            }
            throw new Error(message);
        } catch {
            return { done: 'failure', message: 'Profile updated successfully, but failed to update user roles.' };
        }
    };
    const saveExistingAccount = async (): Promise<SaveResult> => {
        const { hasProfileChanges, editData, hasRoleChanges, roleIds } = driveEditInfo();
        let profileResult: SaveResult | null = null;
        if (hasProfileChanges) {
            profileResult = await submitProfileEdit(id as string, editData);
            if (profileResult.done !== 'success') {
                return profileResult;
            }
        }

        if (hasRoleChanges) {
            const roleResult = await submitRoleSync(roleIds);
            if (roleResult.done === 'success' && profileResult) {
                return { done: 'success', message: t('Editing Profile and User Roles updated successfully.') };
            }
            return roleResult;
        }

        return profileResult ?? { done: 'no_changes', message: t('No changes detected.') };
    };

    const createNewAccount = async (): Promise<SaveResult> => {
        const corporationId = (await localforage.getItem('corporation_id')) as number;
        const clusterIds = sForm.clusters.map((cluster) => cluster.id);
        try {
            const payload = {
                ...sForm,
                corporate_id: corporationId,
                user_cluster: clusterIds,
                role_ids: sForm.user_level === NORMAL_USER_LEVEL ? sForm.userRoleIds : [],
            };
            const {
                data: { data, message, isSuccess, status },
            } = await createAccountAPI(payload);

            if (status === 200 && isSuccess) {
                return { done: 'success', message: 'Create successful!' };
            }
            if (status === 400 && message === 'Validation failed.' && Array.isArray(data)) {
                return { done: 'validation', message: 'Validation failed.', validationMessage: data };
            }
            throw new Error(message);
        } catch (error) {
            const errorReason = error instanceof Error ? error.message : 'Create failed!';
            return { done: 'failure', message: errorReason };
        }
    };

    const handleSave = async () => {
        if (!isAccountReady) return;
        const hasErrors = validateForm();
        if (hasErrors) return;

        dispatch({ type: 'SET_SUBMITTING', payload: true });
        const { done, message, validationMessage } = id ? await saveExistingAccount() : await createNewAccount();
        dispatch({ type: 'SET_SUBMITTING', payload: false });

        if (done === 'success' || done === 'no_changes') {
            nav('/admin/account');
            dispatch({ type: 'RESET' });
            setNotification({ message: t(message), severity: done === 'success' ? 'success' : 'info' });
            return;
        }
        if (done === 'validation') {
            setAPIValidationMessage(validationMessage as ValidationMessage[]);
        }
        setNotification({ message: t(message), severity: 'error' });
    };

    const isFieldError = (field: keyof AdminAccountDialogValidation) => {
        return validatedMessage[field] !== '';
    };

    const handleInputForm = (field: string, e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = e.target.value;
        dispatch({ type: 'SET_FORM_FIELD', field, value });
    };

    const handleChangeUserLevel = (e: SelectChangeEvent<number>) => {
        dispatch({ type: 'SET_USER_LEVEL', payload: Number(e.target.value) });
    };

    const handleCluster = (_: SyntheticEvent<Element, Event>, value: BaseCluster[]) => {
        dispatch({ type: 'SET_CLUSTERS', payload: sortBy(value, 'id') });
    };

    const handleCheckbox = (e: SyntheticEvent, checked: boolean) => {
        const value = Number((e.target as HTMLInputElement).value);
        dispatch({ type: 'TOGGLE_ROLE', payload: { roleId: value, checked } });
    };

    return (
        <>
            <BasePaper className={'admin-paper'}>
                <BaseTitle icon={<DomainAdd />} title={displayTitle} />

                <BaseCard>
                    <BaseLoading open={pageLoading || submitting} />
                    <Box sx={fieldsGridSx}>
                        {FIELD_CONFIG.map((field, index) => {
                            return (
                                <BaseLabelFieldControl
                                    key={`${field.htmlFor}_${index}`}
                                    htmlFor={field.htmlFor}
                                    label={t(field.label)}
                                    required={field.required}
                                    error={isFieldError(field.htmlFor)}
                                    helperText={validatedMessage[field.htmlFor]}
                                >
                                    {field.type === 'input' && (
                                        <OutlinedInput
                                            id={field.htmlFor}
                                            size="small"
                                            placeholder={t(field.label)}
                                            value={sForm[field.htmlFor as keyof typeof sForm] as string}
                                            onChange={(e) => handleInputForm(field.htmlFor, e)}
                                            error={isFieldError(field.htmlFor)}
                                            inputProps={field.inputProps}
                                        />
                                    )}
                                    {field.type === 'select' && (
                                        <Select<number>
                                            id={field.htmlFor}
                                            size="small"
                                            value={
                                                userLevelOptions.some((o) => o.value === sForm.user_level)
                                                    ? sForm.user_level
                                                    : ''
                                            }
                                            onChange={handleChangeUserLevel}
                                        >
                                            {userLevelOptions.map((userLevel) => (
                                                <MenuItem key={userLevel.value} value={userLevel.value}>
                                                    {t(userLevel.label)}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    )}
                                    {field.type === 'autocomplete' && (
                                        <BaseAutocomplete<BaseCluster, true>
                                            id={field.htmlFor}
                                            multiple
                                            options={clusterList}
                                            getOptionLabel={(option) => (option as BaseCluster).cluster_name}
                                            isOptionEqualToValue={(option, value) => option.id === value.id}
                                            value={sForm.clusters ?? []}
                                            onChange={(e, v) => handleCluster(e, v)}
                                            renderTags={(value, getTagProps) =>
                                                value.map((option, index) => {
                                                    const { key, ...tagProps } = getTagProps({
                                                        index,
                                                    });
                                                    return (
                                                        <Chip
                                                            variant="outlined"
                                                            label={option.cluster_name}
                                                            key={key}
                                                            {...tagProps}
                                                        />
                                                    );
                                                })
                                            }
                                            renderInput={(params) => (
                                                <TextField {...params} placeholder={t('Cluster')} size="small" />
                                            )}
                                        />
                                    )}
                                </BaseLabelFieldControl>
                            );
                        })}
                    </Box>

                    <Box sx={passwordCardSx}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Lock sx={{ color: 'text.secondary' }} />
                            <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }} color="text.primary">
                                    {t('Password')} *
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    {isPasswordSet ? t('Password Set') : t('Not set')}
                                </Typography>
                            </Box>
                        </Box>
                        <BaseButton variant="outlined" onClick={() => setOpenPwdDialog(true)}>
                            {id ? t('Change Password') : t('New Password')}
                        </BaseButton>
                    </Box>
                    {showRoles && (
                        <>
                            <Divider sx={{ my: 3 }} />
                            <Typography variant="subtitle2" component="h2" sx={{ fontWeight: 600, mb: 1.5 }}>
                                {t('Roles')}
                            </Typography>
                            <FormGroup sx={rolesFormGroupSx}>
                                {allRoleList.map((ael) => {
                                    const raFind = sForm.userRoleIds.find((id) => id == ael.role_id);
                                    const disableCheckbox = !checkPermission('page-adminRole-all-write');
                                    return (
                                        <FormControlLabel
                                            key={'ability_' + ael.role_id}
                                            control={
                                                <Checkbox disabled={disableCheckbox} checked={raFind != undefined} />
                                            }
                                            label={ael.role_name}
                                            onChange={handleCheckbox}
                                            value={ael.role_id}
                                        />
                                    );
                                })}
                            </FormGroup>
                        </>
                    )}
                    <Box sx={{ my: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <Link to="/admin/account">
                            <BaseButton variant="outlined" startIcon={<ArrowBack />} color="secondary">
                                {t('Back')}
                            </BaseButton>
                        </Link>
                        <BaseButton
                            variant="contained"
                            startIcon={id ? <Check /> : <Add />}
                            disabled={pageLoading || submitting || !isAccountReady}
                            onClick={() => handleSave()}
                        >
                            {confirmTitle}
                        </BaseButton>
                    </Box>
                </BaseCard>
            </BasePaper>
            <AdminChangePasswordDialog
                id={id}
                close={() => setOpenPwdDialog(false)}
                open={openPwdDialog}
                sForm={sForm}
                setPageLoading={(loading: boolean) => dispatch({ type: 'SET_PAGE_LOADING', payload: loading })}
                setSForm={(update) => dispatch({ type: 'UPDATE_FORM', payload: update })}
            />
        </>
    );
};

export default AdminAccountPageDialog;
